import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Dropdown, Icon, Input, Modal, ModalSize } from '@column/column-ui-kit';
import { Badge, DataTable, type DataTableColumnDef } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { NotificationList } from '~/components/NotificationList';
import { RenderFields } from '~/components/RenderFields';
import { Empty as TableEmpty, getTimestampColumn, tableDefaults, useTimeFormat } from '~/components/Table';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardProgram, CardProgramCreditPolicy, CardProgramRepository } from '~/repositories/CardProgramRepository';
import { CreateCardProgramRequest, SimulateRepository } from '~/repositories/SimulateRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

interface CardProgramForm {
  type: CreateCardProgramRequest['type'];
  creditPolicy: CardProgramCreditPolicy;
  description: string;
}

const CARD_PROGRAM_TYPE_OPTIONS = [
  { label: 'Debit', value: 'debit' },
  { label: 'Credit', value: 'credit' },
  { label: 'Charge', value: 'charge' },
];

const CREDIT_POLICY_OPTIONS = [
  { label: 'Unsecured', value: 'unsecured' },
  { label: 'Cardholder Secured', value: 'cardholder_secured' },
  { label: 'Platform Secured', value: 'platform_secured' },
];

export const PageCardProgramsOverview: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentPlatform, isSandbox } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('card-programs');

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const formMethods = useForm<CardProgramForm>({
    defaultValues: {
      type: 'debit',
      creditPolicy: 'unsecured',
      description: '',
    },
  });
  const { watch } = formMethods;
  const selectedType = watch('type');
  const isCreditOrCharge = selectedType === 'credit' || selectedType === 'charge';

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['card-programs', 'list', currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardProgramRepository.listCardPrograms({ limit: pageSize, startingAfter: cursor }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });
  const cardPrograms = data?.cardPrograms ?? [];
  const hasMore = Boolean(data?.hasMore);

  const handleOpenModal = useCallback(() => {
    formMethods.reset({ type: 'debit', creditPolicy: 'unsecured', description: '' });
    setIsModalOpen(true);
  }, [formMethods]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onSubmit = useCallback(
    async (formData: CardProgramForm) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        const request: CreateCardProgramRequest = { type: formData.type };
        if (formData.description.trim()) {
          request.description = formData.description.trim();
        }
        if (formData.type === 'credit' || formData.type === 'charge') {
          request.creditPolicy = formData.creditPolicy;
        }

        await SimulateRepository.createCardProgram(request);
        handleCloseModal();
        addSuccessNotification({ content: 'Card program created' });
        queryClient.invalidateQueries({ queryKey: ['card-programs'] });
      } catch (err: any) {
        addDangerNotification({ content: err?.message || 'Failed to create card program', display: 'popup' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, handleCloseModal, addSuccessNotification, addDangerNotification, queryClient]
  );

  const columns: DataTableColumnDef<CardProgram>[] = [
    {
      header: 'Name',
      accessor: 'description',
      width: 'minmax(min-content, 2fr)',
      cell: ({ row }) => <DataTable.TextCell weight="medium" value={row.description || row.id} />,
    },
    {
      header: 'Network',
      accessor: 'primaryCardScheme',
      width: 'minmax(min-content, 1fr)',
      cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
    },
    {
      header: 'BIN',
      accessor: 'bin',
      width: 'minmax(min-content, 1fr)',
      cell: ({ value }) => <DataTable.TextCell value={(value as string) || '-'} />,
    },
    {
      header: 'Type',
      accessor: 'cardProgramType',
      width: 'min-content',
      cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
    },
    {
      header: 'Status',
      accessor: 'status',
      width: 'min-content',
      cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
    },
    getTimestampColumn<CardProgram>({ accessor: 'createdAt', timeFormat, onTimeFormatChange, fallback: '-' }),
  ];

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} size={ModalSize.Small}>
        <Modal.IconCircle icon="AnimationPiggy" type="primary" />
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <Modal.Content beforeContent={<NotificationList display="popup" variant="light" />}>
              <Modal.Headline align="center">Create Card Program</Modal.Headline>
              <Modal.Paragraph align="center">Create a sandbox Visa card program</Modal.Paragraph>
              {RenderFields<CardProgramForm>({
                sections: [
                  {
                    variant: 'isCompact',
                    fields: [
                      {
                        id: 'type',
                        label: 'Type',
                        children: ({ value, onChange }) => (
                          <Dropdown
                            active={String(value)}
                            onChange={onChange}
                            options={CARD_PROGRAM_TYPE_OPTIONS}
                            fullWidth
                          />
                        ),
                      },
                      {
                        id: 'creditPolicy',
                        label: 'Credit Policy',
                        description: 'How credit extended under this program is secured',
                        hide: !isCreditOrCharge,
                        rules: { required: isCreditOrCharge },
                        children: ({ value, onChange }, { isTouched, error }) => (
                          <Dropdown
                            active={value ? String(value) : undefined}
                            onChange={onChange}
                            options={CREDIT_POLICY_OPTIONS}
                            fullWidth
                            hasError={isTouched && !!error}
                          />
                        ),
                      },
                      {
                        id: 'description',
                        label: 'Description',
                        children: ({ value, onChange }) => (
                          <Input value={String(value)} onChange={onChange} placeholder="Description (optional)" />
                        ),
                      },
                    ],
                  },
                ],
              })}
            </Modal.Content>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal} size="small" type="button">
                Cancel
              </Button>
              <Button isLoading={isSubmitting} isDisabled={isSubmitting} size="small" type="submit">
                Create Program
              </Button>
            </Modal.Footer>
          </form>
        </FormProvider>
      </Modal>

      <PageActions>
        {isSandbox && (
          <Button onClick={handleOpenModal} size="small" icon={<Icon.Sandbox />}>
            Create Program
          </Button>
        )}
      </PageActions>
      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<CardProgram>
            {...tableDefaults}
            columns={columns}
            data={isError ? [] : cardPrograms}
            rowKey={(row) => row.id}
            isLoading={isFetching}
            emptyContent={
              isError ? (
                <TableEmpty icon={<Icon.Warning />} title="Failed to load card programs" />
              ) : (
                <TableEmpty
                  icon={<Icon.AnimationWallet />}
                  title="No card programs found"
                  description="Card programs will appear here once they're created."
                  action={
                    isSandbox && (
                      <Button onClick={handleOpenModal} size="small" icon={<Icon.Sandbox />}>
                        Create Program
                      </Button>
                    )
                  }
                />
              )
            }
            pagination={{
              hasNext: hasMore,
              hasPrevious,
              pageIndex,
              pageSize,
              onNext: () => {
                if (cardPrograms.length > 0) {
                  onNext({ cursor: cardPrograms[cardPrograms.length - 1].id });
                }
              },
              onPrevious,
              onPageSizeChange: setPageSize,
            }}
            onRowClick={(row) => {
              navigate(`${ROUTE.CARD_PROGRAMS}/edit/${row.id}`);
            }}
          />
        </Inner>
      </Loading>
    </>
  );
};
